import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Euro, Send, Bot, Sparkles, Filter, Check, X, 
  FileText, ExternalLink, Calendar, MapPin, Leaf,
  Tractor, Building, ChevronDown, ChevronUp, RefreshCw,
  Search, AlertCircle, CheckCircle, Clock, Loader2, Heart, Calculator, Settings
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { SpeakButtonCompact } from '../components/SpeakButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Horse icon component
const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 8c-1.5 1-3.5 1-5 0-1.5-1-3.5-1-5 0s-3.5 1-5 0c-1.5-1-3.5-1-5 0"/>
    <path d="M5.5 18.5 2 22"/>
    <path d="m18.5 18.5 3.5 3.5"/>
    <path d="M12 22v-8"/>
    <path d="M8 22V10"/>
    <path d="M16 22V10"/>
    <path d="M12 14c-2-2-2-4 0-6s4 0 4 2"/>
    <path d="M12 8V2l2 2"/>
  </svg>
);

// Catégories d'aides - DYNAMIQUES selon filière
const CATEGORIES_AIDES_BASE = [
  { id: 'pac', label: 'PAC & Aides directes', icon: FileText, color: 'bg-blue-500' },
  { id: 'bio', label: 'Agriculture Biologique', icon: Leaf, color: 'bg-green-500' },
  { id: 'environnement', label: 'Environnement & MAEC', icon: Sparkles, color: 'bg-emerald-500' },
  { id: 'investissement', label: 'Investissements', icon: Tractor, color: 'bg-indigo-500' },
  { id: 'installation', label: 'Installation & JA', icon: Building, color: 'bg-purple-500' },
  { id: 'calamites', label: 'Calamités & Crises', icon: AlertCircle, color: 'bg-red-500' }
];

// Catégories spécifiques par filière
const CATEGORIES_PAR_FILIERE = {
  equin: { id: 'equin', label: 'Filière Équine', icon: Heart, color: 'bg-amber-500', isHorse: true },
  eta: { id: 'eta', label: 'ETA & Prestation', icon: Tractor, color: 'bg-orange-500' },
  viticulture: { id: 'viticulture', label: 'Viticulture & Vins', icon: Leaf, color: 'bg-purple-500' },
  arboriculture: { id: 'arboriculture', label: 'Arboriculture', icon: Leaf, color: 'bg-green-600' },
  ppam: { id: 'ppam', label: 'PPAM & Distillation', icon: Sparkles, color: 'bg-pink-500' },
  grandes_cultures: { id: 'grandes_cultures', label: 'Grandes Cultures', icon: FileText, color: 'bg-yellow-500' },
  elevage: { id: 'elevage', label: 'Élevage', icon: Heart, color: 'bg-red-400' },
  polyculture: { id: 'polyculture', label: 'Polyculture-Élevage', icon: Tractor, color: 'bg-teal-500' }
};

// Fonction pour obtenir les catégories selon la filière du workspace
const getCategoriesAides = (filiere) => {
  const categories = [...CATEGORIES_AIDES_BASE];
  if (filiere && CATEGORIES_PAR_FILIERE[filiere]) {
    // Insérer la catégorie spécifique en 2ème position
    categories.splice(1, 0, CATEGORIES_PAR_FILIERE[filiere]);
  }
  return categories;
};

// Profils d'exploitation - DYNAMIQUES selon filière
const PROFILS_BASE = [
  { id: 'bio', label: 'Certifié Bio' },
  { id: 'hve', label: 'Certifié HVE' },
  { id: 'jeune_agriculteur', label: 'Jeune Agriculteur' },
  { id: 'zone_defavorisee', label: 'Zone défavorisée' },
  { id: 'zone_montagne', label: 'Zone montagne' },
  { id: 'irrigation', label: 'Irrigation' }
];

const PROFILS_PAR_FILIERE = {
  equin: [
    { id: 'equin_elevage', label: 'Élevage Équin', isHorse: true },
    { id: 'equin_pension', label: 'Pension / Centre Équestre', isHorse: true },
    { id: 'equin_debourrage', label: 'Débourrage & Dressage', isHorse: true },
    { id: 'equin_sport', label: 'Chevaux de Sport', isHorse: true },
    { id: 'equin_trait', label: 'Chevaux de Trait', isHorse: true }
  ],
  eta: [
    { id: 'eta_travaux', label: 'Travaux agricoles' },
    { id: 'eta_transport', label: 'Transport agricole' },
    { id: 'eta_moisson', label: 'Moisson / Battage' },
    { id: 'eta_epandage', label: 'Épandage' },
    { id: 'eta_semis', label: 'Semis / Plantation' }
  ],
  viticulture: [
    { id: 'viti_aoc', label: 'AOC / AOP' },
    { id: 'viti_igp', label: 'IGP' },
    { id: 'viti_cave', label: 'Cave particulière' },
    { id: 'viti_coop', label: 'Coopérative' },
    { id: 'viti_bio', label: 'Viticulture Bio' }
  ],
  arboriculture: [
    { id: 'arbo_pomme', label: 'Pommes / Poires' },
    { id: 'arbo_noyaux', label: 'Fruits à noyaux' },
    { id: 'arbo_noix', label: 'Noix / Châtaignes' },
    { id: 'arbo_petits_fruits', label: 'Petits fruits' }
  ],
  ppam: [
    { id: 'ppam_lavande', label: 'Lavande / Lavandin' },
    { id: 'ppam_aromatiques', label: 'Plantes aromatiques' },
    { id: 'ppam_medicinales', label: 'Plantes médicinales' },
    { id: 'ppam_distillation', label: 'Distillation' }
  ],
  grandes_cultures: [
    { id: 'gc_cereales', label: 'Céréales' },
    { id: 'gc_oleagineux', label: 'Oléagineux' },
    { id: 'gc_proteagineux', label: 'Protéagineux' },
    { id: 'gc_betterave', label: 'Betteraves' }
  ],
  elevage: [
    { id: 'elev_bovin', label: 'Bovins' },
    { id: 'elev_ovin', label: 'Ovins / Caprins' },
    { id: 'elev_porcin', label: 'Porcins' },
    { id: 'elev_volaille', label: 'Volailles' }
  ],
  polyculture: [
    { id: 'poly_cultures', label: 'Grandes cultures' },
    { id: 'poly_elevage', label: 'Élevage' },
    { id: 'poly_diversification', label: 'Diversification' }
  ]
};

// Fonction pour obtenir les profils selon la filière
const getProfilsExploitation = (filiere) => {
  const profils = [];
  if (filiere && PROFILS_PAR_FILIERE[filiere]) {
    profils.push(...PROFILS_PAR_FILIERE[filiere]);
  }
  profils.push(...PROFILS_BASE);
  return profils;
};

// AIDES ÉQUINES SPÉCIFIQUES
const AIDES_EQUINES = [
  {
    id: 'ifce_jument',
    nom: 'Prime à la Jument',
    organisme: 'IFCE / France Galop',
    montant: '300 à 800 €/jument',
    description: 'Prime pour les juments poulinières inscrites au SIRE produisant des poulains',
    conditions: ['Jument inscrite au SIRE', 'Production de poulain déclaré', 'Respect du cahier des charges racial'],
    date_limite: '31 décembre',
    lien: 'https://www.ifce.fr',
    categorie: 'elevage'
  },
  {
    id: 'fonds_eperon',
    nom: 'Fonds ÉPERON',
    organisme: 'Fonds ÉPERON / FFE',
    montant: 'Variable (projets)',
    description: 'Soutien aux projets innovants de la filière équine : infrastructures, formations, bien-être animal',
    conditions: ['Projet structurant pour la filière', 'Dossier de candidature', 'Conventionnement'],
    date_limite: 'Appels à projets réguliers',
    lien: 'https://www.fondseperon.com',
    categorie: 'investissement'
  },
  {
    id: 'region_equin',
    nom: 'Aide Régionale Filière Équine',
    organisme: 'Région Auvergne-Rhône-Alpes',
    montant: 'Jusqu\'à 40% des investissements',
    description: 'Aide aux investissements pour les structures équines : boxes, carrières, manèges',
    conditions: ['Siège en région AURA', 'Projet d\'investissement > 5000€', 'Statut agricole'],
    date_limite: 'Permanent',
    lien: 'https://www.auvergnerhonealpes.fr',
    categorie: 'investissement'
  },
  {
    id: 'ichn_equin',
    nom: 'ICHN Équidés',
    organisme: 'ASP / PAC',
    montant: '70 à 150 €/ha de prairie',
    description: 'Indemnité Compensatoire de Handicaps Naturels pour les surfaces en herbe pâturées par les équidés',
    conditions: ['Zone défavorisée ou montagne', 'Chargement UGB adapté', 'Déclaration PAC'],
    date_limite: '15 mai (déclaration PAC)',
    lien: 'https://www.telepac.agriculture.gouv.fr',
    categorie: 'pac'
  },
  {
    id: 'maec_prairie',
    nom: 'MAEC Prairies & Équidés',
    organisme: 'ASP / DRAAF',
    montant: '100 à 350 €/ha',
    description: 'Mesures Agro-Environnementales pour la gestion des prairies avec pâturage équin',
    conditions: ['Engagement 5 ans', 'Respect du cahier des charges', 'Formation obligatoire'],
    date_limite: '15 mai (déclaration PAC)',
    lien: 'https://agriculture.gouv.fr/maec',
    categorie: 'environnement'
  },
  {
    id: 'dja_equin',
    nom: 'DJA Spécifique Équin',
    organisme: 'DDT / Région',
    montant: '10 000 à 40 000 €',
    description: 'Dotation Jeune Agriculteur majorée pour installation en activité équine',
    conditions: ['Moins de 40 ans', 'Diplôme agricole niveau 4', 'Plan d\'entreprise viable'],
    date_limite: 'Permanent',
    lien: 'https://www.jeunes-agriculteurs.fr',
    categorie: 'installation'
  },
  {
    id: 'pcae_equin',
    nom: 'PCAE Bâtiments Équins',
    organisme: 'Région / FEADER',
    montant: '20 à 40% des travaux',
    description: 'Plan de Compétitivité et d\'Adaptation des Exploitations pour constructions équines',
    conditions: ['Projet > 10 000 €', 'Exploitation agricole', 'Permis de construire si nécessaire'],
    date_limite: 'Appels à projets',
    lien: 'https://www.europe-en-france.gouv.fr',
    categorie: 'investissement'
  },
  {
    id: 'bien_etre_equin',
    nom: 'Aide Bien-Être Équin',
    organisme: 'IFCE / Conseil des Chevaux',
    montant: 'Jusqu\'à 5 000 €',
    description: 'Aide pour améliorer les conditions de vie des équidés : abris, abreuvoirs, clôtures',
    conditions: ['Évaluation bien-être préalable', 'Investissement minimum 2000€', 'Suivi vétérinaire'],
    date_limite: 'Variable selon région',
    lien: 'https://www.ifce.fr/bien-etre',
    categorie: 'investissement'
  }
];

export default function BotSubventionsPage() {
  const { activeSociete } = useWorkspace();
  const navigate = useNavigate();
  const activeSocieteId = activeSociete?.id;
  const messagesEndRef = useRef(null);
  
  // Déterminer la filière du workspace actif
  const filiereWorkspace = activeSociete?.filiere || 'polyculture';
  
  // Obtenir les catégories et profils dynamiques selon la filière
  const CATEGORIES_AIDES = getCategoriesAides(filiereWorkspace);
  const PROFILS_EXPLOITATION = getProfilsExploitation(filiereWorkspace);
  
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Configuration du profil (filtrage avancé)
  const [showConfig, setShowConfig] = useState(true);
  const [profilExploitation, setProfilExploitation] = useState([]);
  const [categoriesActives, setCategoriesActives] = useState(['pac', 'bio', 'environnement']);
  const [surfaceTotale, setSurfaceTotale] = useState('');
  const [departement, setDepartement] = useState('');
  const [configured, setConfigured] = useState(false);
  
  // Suggestions d'aides
  const [suggestions, setSuggestions] = useState([]);
  const [aidesEnCours, setAidesEnCours] = useState([]);

  // Afficher le simulateur uniquement si filière équine
  const isEquin = filiereWorkspace === 'equin';

  // SIMULATEUR ÉQUIN (visible seulement pour filière équine)
  const [showSimulateur, setShowSimulateur] = useState(false);
  const [simNbChevaux, setSimNbChevaux] = useState(7);
  const [simNbJuments, setSimNbJuments] = useState(2);
  const [simNbPoulains, setSimNbPoulains] = useState(0);
  const [simSurfacePrairie, setSimSurfacePrairie] = useState(15);
  const [simZoneDefavorisee, setSimZoneDefavorisee] = useState(false);
  const [simInvestissement, setSimInvestissement] = useState(0);

  // Calcul des aides équines
  const calculerAidesEquines = () => {
    let total = 0;
    const details = [];

    // Prime à la Jument (300-800€/jument produisant)
    if (simNbJuments > 0) {
      const primeJument = simNbJuments * 500; // moyenne 500€
      total += primeJument;
      details.push({ nom: 'Prime à la Jument', montant: primeJument, detail: `${simNbJuments} jument(s) × 500€` });
    }

    // Prime Poulain (200-500€/poulain)
    if (simNbPoulains > 0) {
      const primePoulain = simNbPoulains * 350;
      total += primePoulain;
      details.push({ nom: 'Prime au Poulain', montant: primePoulain, detail: `${simNbPoulains} poulain(s) × 350€` });
    }

    // ICHN si zone défavorisée (70-150€/ha prairie)
    if (simZoneDefavorisee && simSurfacePrairie > 0) {
      const ichn = simSurfacePrairie * 110; // moyenne 110€/ha
      total += ichn;
      details.push({ nom: 'ICHN Zone défavorisée', montant: ichn, detail: `${simSurfacePrairie} ha × 110€` });
    }

    // MAEC Prairies (100-350€/ha)
    if (simSurfacePrairie > 0) {
      const maec = simSurfacePrairie * 200; // moyenne 200€/ha
      total += maec;
      details.push({ nom: 'MAEC Prairies équidés', montant: maec, detail: `${simSurfacePrairie} ha × 200€` });
    }

    // Éco-régime (60-82€/ha)
    if (simSurfacePrairie > 0) {
      const ecoRegime = simSurfacePrairie * 70;
      total += ecoRegime;
      details.push({ nom: 'Éco-régime niveau 2', montant: ecoRegime, detail: `${simSurfacePrairie} ha × 70€` });
    }

    // PCAE Investissement (20-40% des travaux)
    if (simInvestissement > 0) {
      const pcae = simInvestissement * 0.30; // 30% moyenne
      total += pcae;
      details.push({ nom: 'PCAE Bâtiments', montant: pcae, detail: `30% de ${simInvestissement.toLocaleString()}€` });
    }

    return { total, details };
  };

  const resultatsSimulation = calculerAidesEquines();

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'X-Societe-Id': activeSocieteId || localStorage.getItem('activeSocieteId') || ''
  });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleProfil = (profilId) => {
    setProfilExploitation(prev => 
      prev.includes(profilId) 
        ? prev.filter(p => p !== profilId)
        : [...prev, profilId]
    );
  };

  const toggleCategorie = (catId) => {
    setCategoriesActives(prev => 
      prev.includes(catId)
        ? prev.filter(c => c !== catId)
        : [...prev, catId]
    );
  };

  const startAssistant = async () => {
    if (profilExploitation.length === 0) {
      alert('Veuillez sélectionner au moins un profil d\'exploitation');
      return;
    }
    
    setConfigured(true);
    setShowConfig(false);
    
    // Message de bienvenue personnalisé
    const profilsLabels = profilExploitation.map(p => 
      PROFILS_EXPLOITATION.find(pr => pr.id === p)?.label
    ).join(', ');
    
    const welcomeMessage = {
      role: 'assistant',
      content: `🎯 **Bot Subventions & Aides activé !**

J'ai analysé votre profil :
- **Profil** : ${profilsLabels}
- **Surface** : ${surfaceTotale || 'Non précisée'} ha
- **Département** : ${departement || 'Non précisé'}
- **Catégories surveillées** : ${categoriesActives.map(c => CATEGORIES_AIDES.find(ca => ca.id === c)?.label).join(', ')}

Je vais maintenant rechercher les aides auxquelles vous êtes potentiellement éligible. Posez-moi vos questions ou demandez-moi de lancer une recherche complète !

**Exemples de questions :**
- "Quelles aides pour mon exploitation bio ?"
- "Comment demander l'éco-régime niveau 2 ?"
- "Aides disponibles pour l'irrigation ?"
- "Calendrier des demandes PAC 2026"`,
      timestamp: new Date().toISOString()
    };
    
    setMessages([welcomeMessage]);
    
    // Lancer la recherche d'aides automatique
    await searchAides();
  };

  const searchAides = async () => {
    setLoading(true);
    
    // Simuler une recherche d'aides (à connecter au backend)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Suggestions basées sur le profil
    const mockSuggestions = [];
    
    if (profilExploitation.includes('bio')) {
      mockSuggestions.push({
        id: 'aide-bio-1',
        titre: 'Aide à la conversion bio',
        montant: '350€/ha/an pendant 5 ans',
        categorie: 'bio',
        eligibilite: 'haute',
        description: 'Aide pour les exploitations en conversion vers l\'agriculture biologique',
        deadline: '15 mai 2026',
        url: 'https://agriculture.gouv.fr/aide-bio'
      });
      mockSuggestions.push({
        id: 'aide-bio-2',
        titre: 'Crédit d\'impôt bio',
        montant: 'Jusqu\'à 3 500€/an',
        categorie: 'bio',
        eligibilite: 'haute',
        description: 'Crédit d\'impôt pour les exploitations certifiées bio',
        deadline: 'Déclaration fiscale',
        url: null
      });
    }
    
    if (profilExploitation.includes('jeune_agriculteur')) {
      mockSuggestions.push({
        id: 'aide-ja-1',
        titre: 'Dotation Jeune Agriculteur (DJA)',
        montant: '10 000€ à 40 000€',
        categorie: 'installation',
        eligibilite: 'haute',
        description: 'Aide à l\'installation pour les agriculteurs de moins de 40 ans',
        deadline: 'Dépôt continu',
        url: null
      });
    }
    
    if (categoriesActives.includes('pac')) {
      mockSuggestions.push({
        id: 'aide-pac-1',
        titre: 'Aide de base au revenu (ABR)',
        montant: '~120€/ha',
        categorie: 'pac',
        eligibilite: 'haute',
        description: 'Aide découplée versée à tous les agriculteurs actifs',
        deadline: '15 mai 2026',
        url: null
      });
      mockSuggestions.push({
        id: 'aide-pac-2',
        titre: 'Éco-régime',
        montant: '60€ à 82€/ha selon niveau',
        categorie: 'pac',
        eligibilite: profilExploitation.includes('bio') || profilExploitation.includes('hve') ? 'haute' : 'moyenne',
        description: 'Rémunération des pratiques favorables à l\'environnement',
        deadline: '15 mai 2026',
        url: null
      });
    }
    
    if (categoriesActives.includes('environnement')) {
      mockSuggestions.push({
        id: 'aide-maec-1',
        titre: 'MAEC - Haies et biodiversité',
        montant: '150€ à 450€/ha',
        categorie: 'environnement',
        eligibilite: 'moyenne',
        description: 'Mesures agro-environnementales pour la biodiversité',
        deadline: '15 mai 2026',
        url: null
      });
    }
    
    if (profilExploitation.includes('zone_defavorisee') || profilExploitation.includes('zone_montagne')) {
      mockSuggestions.push({
        id: 'aide-ichn-1',
        titre: 'ICHN - Indemnité compensatoire',
        montant: '70€ à 280€/ha',
        categorie: 'pac',
        eligibilite: 'haute',
        description: 'Compensation pour les zones à handicaps naturels',
        deadline: '15 mai 2026',
        url: null
      });
    }

    // AIDES ÉQUINES
    const isEquin = profilExploitation.some(p => p.startsWith('equin_')) || categoriesActives.includes('equin');
    if (isEquin) {
      // Ajouter toutes les aides équines pertinentes
      AIDES_EQUINES.forEach(aide => {
        mockSuggestions.push({
          id: aide.id,
          titre: aide.nom,
          montant: aide.montant,
          categorie: 'equin',
          eligibilite: 'haute',
          description: aide.description,
          deadline: aide.date_limite,
          url: aide.lien,
          organisme: aide.organisme,
          conditions: aide.conditions,
          isEquin: true
        });
      });
    }

    // Aides spécifiques selon le type d'activité équine
    if (profilExploitation.includes('equin_elevage')) {
      mockSuggestions.push({
        id: 'equin-elevage-1',
        titre: 'Prime au Poulain Né & Élevé en France',
        montant: '200 à 500€/poulain',
        categorie: 'equin',
        eligibilite: 'haute',
        description: 'Prime pour les naissances de poulains déclarés au SIRE',
        deadline: '30 jours après naissance',
        url: 'https://www.ifce.fr',
        isEquin: true
      });
    }

    if (profilExploitation.includes('equin_trait')) {
      mockSuggestions.push({
        id: 'equin-trait-1',
        titre: 'Aide Chevaux de Trait - Conservation',
        montant: '400 à 800€/jument',
        categorie: 'equin',
        eligibilite: 'haute',
        description: 'Aide pour la préservation des races de trait françaises',
        deadline: 'Permanent',
        url: 'https://www.france-trait.fr',
        isEquin: true
      });
    }

    if (profilExploitation.includes('equin_pension')) {
      mockSuggestions.push({
        id: 'equin-pension-1',
        titre: 'Aide Modernisation Centres Équestres',
        montant: 'Jusqu\'à 30% des travaux',
        categorie: 'equin',
        eligibilite: 'moyenne',
        description: 'Aide pour la rénovation et mise aux normes des installations',
        deadline: 'Appels à projets',
        url: 'https://www.ffe.com',
        isEquin: true
      });
    }
    
    setSuggestions(mockSuggestions);
    setLoading(false);
    
    // Ajouter un message avec les résultats
    if (mockSuggestions.length > 0) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `🔍 **Recherche terminée !**

J'ai identifié **${mockSuggestions.length} aides** potentiellement accessibles pour votre exploitation. Consultez la liste ci-dessous et n'hésitez pas à me demander des précisions sur chacune d'elles.

${mockSuggestions.filter(s => s.eligibilite === 'haute').length > 0 ? `⭐ **${mockSuggestions.filter(s => s.eligibilite === 'haute').length} aides à forte éligibilité** - Je vous recommande de les examiner en priorité.` : ''}`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;
    
    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    
    try {
      const res = await api.post('/brain/chat', {
        message: inputMessage,
        agent_type: 'subventions',
        context: {
          profil_exploitation: profilExploitation,
          categories_actives: categoriesActives,
          surface_totale: surfaceTotale,
          departement: departement,
          suggestions_actuelles: suggestions.map(s => s.titre)
        }
      });
      
      const data = res.data;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response || data.message,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Erreur:', error);
    }
    
    setLoading(false);
  };

  const demanderAide = (aide) => {
    setAidesEnCours(prev => [...prev, aide.id]);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `✅ **Aide "${aide.titre}" ajoutée à votre liste de suivi !**

**Prochaines étapes :**
1. Vérifiez votre éligibilité complète
2. Préparez les documents nécessaires
3. Déposez votre demande avant le ${aide.deadline}

Voulez-vous que je vous aide à préparer le dossier ?`,
      timestamp: new Date().toISOString()
    }]);
  };

  const getEligibiliteColor = (eligibilite) => {
    switch(eligibilite) {
      case 'haute': return 'bg-green-100 text-green-700 border-green-300';
      case 'moyenne': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'basse': return 'bg-gray-100 text-gray-700 border-gray-300';
      default: return 'bg-blue-100 text-blue-700 border-blue-300';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="bot-subventions-page">
      <div className="flex h-[calc(100vh-80px)]">
        {/* Panneau principal */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl">
                  <Euro className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Bot Subventions & Aides</h1>
                  <p className="text-sm text-gray-500">Votre expert en aides agricoles</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/agents/config', { state: { selectedAgent: 'subventions' } })}
                  data-testid="agent-settings-btn"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Paramètres
                </Button>
                {configured && (
                  <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)}>
                    <Filter className="h-4 w-4 mr-2" />
                    {showConfig ? 'Masquer filtres' : 'Modifier profil'}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Configuration initiale ou modifiable */}
          {showConfig && (
            <Card className="m-4 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-600" />
                  Configuration de recherche avancée
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* SECTION ÉQUINE DÉDIÉE */}
                <div className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg border border-amber-300">
                  <div className="flex items-center gap-2 mb-3">
                    <HorseIcon className="h-5 w-5 text-amber-700" />
                    <span className="font-medium text-amber-900">Filière Équine</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {PROFILS_EXPLOITATION.filter(p => p.isHorse).map(profil => (
                      <button
                        key={profil.id}
                        onClick={() => toggleProfil(profil.id)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          profilExploitation.includes(profil.id)
                            ? 'bg-amber-600 text-white border-amber-600'
                            : 'bg-white text-amber-800 border-amber-400 hover:bg-amber-50'
                        }`}
                      >
                        {profilExploitation.includes(profil.id) && <Check className="inline h-3 w-3 mr-1" />}
                        🐴 {profil.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Profil d'exploitation (autres) */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Autres profils d'exploitation</label>
                  <div className="flex flex-wrap gap-2">
                    {PROFILS_EXPLOITATION.filter(p => !p.isHorse).map(profil => (
                      <button
                        key={profil.id}
                        onClick={() => toggleProfil(profil.id)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          profilExploitation.includes(profil.id)
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400'
                        }`}
                      >
                        {profilExploitation.includes(profil.id) && <Check className="inline h-3 w-3 mr-1" />}
                        {profil.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Catégories d'aides */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Catégories d'aides à surveiller</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {CATEGORIES_AIDES.map(cat => {
                      const Icon = cat.icon;
                      const isHorseCategory = cat.isHorse;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => toggleCategorie(cat.id)}
                          className={`p-3 rounded-lg border text-left flex items-center gap-2 transition-all ${
                            categoriesActives.includes(cat.id)
                              ? isHorseCategory 
                                ? 'bg-amber-50 border-amber-500 shadow-md ring-2 ring-amber-300'
                                : 'bg-white border-amber-400 shadow-sm'
                              : 'bg-gray-50 border-gray-200 opacity-60'
                          }`}
                        >
                          <div className={`p-1.5 rounded ${cat.color} text-white`}>
                            {isHorseCategory ? <HorseIcon className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                          </div>
                          <span className={`text-sm ${isHorseCategory ? 'font-medium' : ''}`}>
                            {isHorseCategory ? '🐴 ' : ''}{cat.label}
                          </span>
                          {categoriesActives.includes(cat.id) && (
                            <Check className="h-4 w-4 text-amber-500 ml-auto" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Infos complémentaires - AVEC NOMBRE DE CHEVAUX */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Surface totale (ha)</label>
                    <input
                      type="number"
                      value={surfaceTotale}
                      onChange={(e) => setSurfaceTotale(e.target.value)}
                      placeholder="Ex: 45"
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Département</label>
                    <input
                      type="text"
                      value={departement}
                      onChange={(e) => setDepartement(e.target.value)}
                      placeholder="Ex: 38 ou Isère"
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block flex items-center gap-1">
                      <HorseIcon className="h-4 w-4 text-amber-600" />
                      Nombre de chevaux
                    </label>
                    <input
                      type="number"
                      placeholder="Ex: 7"
                      className="w-full p-2 border rounded-lg border-amber-300 focus:ring-amber-500"
                    />
                  </div>
                </div>

                {!configured && (
                  <Button onClick={startAssistant} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Lancer la recherche d'aides
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Zone de chat */}
          {configured && (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-amber-500 text-white'
                        : 'bg-white border shadow-sm'
                    }`}
                  >
                    <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                    <div className={`text-xs mt-1 ${msg.role === 'user' ? 'text-amber-200' : 'text-gray-400'}`}>
                      {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border rounded-2xl px-4 py-3 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                    <span className="text-sm text-gray-500">Recherche en cours...</span>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input */}
          {configured && (
            <div className="p-4 border-t bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Posez votre question sur les aides agricoles..."
                  className="flex-1 p-3 border rounded-xl focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                  disabled={loading}
                />
                <Button 
                  onClick={sendMessage} 
                  disabled={loading || !inputMessage.trim()}
                  className="bg-amber-500 hover:bg-amber-600 rounded-xl px-6"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Panneau latéral - Suggestions d'aides + SIMULATEUR */}
        {configured && (
          <div className="w-96 border-l bg-white overflow-y-auto">
            {/* Onglets Suggestions / Simulateur */}
            <div className="p-4 border-b sticky top-0 bg-white z-10">
              <div className="flex gap-2 mb-3">
                <Button 
                  variant={!showSimulateur ? "default" : "outline"} 
                  size="sm" 
                  onClick={() => setShowSimulateur(false)}
                  className={!showSimulateur ? "bg-amber-500 hover:bg-amber-600" : ""}
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Aides ({suggestions.length})
                </Button>
                <Button 
                  variant={showSimulateur ? "default" : "outline"} 
                  size="sm" 
                  onClick={() => setShowSimulateur(true)}
                  className={showSimulateur ? "bg-amber-500 hover:bg-amber-600" : ""}
                >
                  <Calculator className="h-4 w-4 mr-1" />
                  Simulateur
                </Button>
              </div>
              {!showSimulateur && (
                <Button variant="ghost" size="sm" onClick={searchAides} className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />Actualiser
                </Button>
              )}
            </div>
            
            {/* SIMULATEUR ÉQUIN */}
            {showSimulateur ? (
              <div className="p-4 space-y-4">
                <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <HorseIcon className="h-5 w-5 text-amber-600" />
                    <h3 className="font-semibold text-amber-900">Simulateur Aides Équines</h3>
                  </div>
                  <p className="text-xs text-amber-700">
                    Estimez le montant total des aides selon votre exploitation
                  </p>
                </div>

                {/* Champs du simulateur */}
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium flex items-center gap-1 mb-1">
                      <HorseIcon className="h-4 w-4 text-amber-600" />
                      Nombre total de chevaux
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={simNbChevaux}
                      onChange={(e) => setSimNbChevaux(parseInt(e.target.value) || 0)}
                      className="w-full p-2 border rounded-lg text-center text-lg font-semibold"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Juments poulinières</label>
                      <input
                        type="number"
                        min="0"
                        value={simNbJuments}
                        onChange={(e) => setSimNbJuments(parseInt(e.target.value) || 0)}
                        className="w-full p-2 border rounded-lg text-center"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Poulains/an</label>
                      <input
                        type="number"
                        min="0"
                        value={simNbPoulains}
                        onChange={(e) => setSimNbPoulains(parseInt(e.target.value) || 0)}
                        className="w-full p-2 border rounded-lg text-center"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium flex items-center gap-1 mb-1">
                      <MapPin className="h-4 w-4 text-green-600" />
                      Surface prairies/pâtures (ha)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={simSurfacePrairie}
                      onChange={(e) => setSimSurfacePrairie(parseFloat(e.target.value) || 0)}
                      className="w-full p-2 border rounded-lg text-center text-lg font-semibold"
                    />
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="zoneDefav"
                      checked={simZoneDefavorisee}
                      onChange={(e) => setSimZoneDefavorisee(e.target.checked)}
                      className="h-4 w-4 text-amber-600 rounded"
                    />
                    <label htmlFor="zoneDefav" className="text-sm">
                      Zone défavorisée / montagne
                    </label>
                  </div>

                  <div>
                    <label className="text-sm font-medium flex items-center gap-1 mb-1">
                      <Building className="h-4 w-4 text-indigo-600" />
                      Investissement prévu (€)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={simInvestissement}
                      onChange={(e) => setSimInvestissement(parseInt(e.target.value) || 0)}
                      placeholder="Ex: 50000"
                      className="w-full p-2 border rounded-lg text-center"
                    />
                    <p className="text-xs text-gray-500 mt-1">Boxes, carrière, bâtiments...</p>
                  </div>
                </div>

                {/* Résultats */}
                <div className="mt-4 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-green-800">Estimation annuelle</span>
                    <span className="text-2xl font-bold text-green-600">
                      {resultatsSimulation.total.toLocaleString()} €
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {resultatsSimulation.details.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{item.nom}</span>
                          <p className="text-xs text-gray-500">{item.detail}</p>
                        </div>
                        <span className="font-semibold text-green-700">
                          {item.montant.toLocaleString()} €
                        </span>
                      </div>
                    ))}
                  </div>

                  {resultatsSimulation.details.length === 0 && (
                    <p className="text-sm text-gray-500 text-center">
                      Ajustez les paramètres pour voir l'estimation
                    </p>
                  )}
                </div>

                <p className="text-xs text-gray-500 text-center">
                  ⚠️ Estimation indicative - Les montants réels dépendent des conditions d'éligibilité
                </p>
              </div>
            ) : (
              /* Liste des aides suggérées */
              <div className="p-4 space-y-3">
                {suggestions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    Lancez une recherche pour voir les aides disponibles
                  </p>
                ) : (
                  suggestions.map(aide => (
                    <Card key={aide.id} className={`border ${aide.isEquin ? 'border-amber-300 bg-amber-50' : ''} ${aidesEnCours.includes(aide.id) ? 'border-green-300 bg-green-50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-sm flex items-center gap-1">
                            {aide.isEquin && <HorseIcon className="h-4 w-4 text-amber-600" />}
                            {aide.titre}
                          </h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${getEligibiliteColor(aide.eligibilite)}`}>
                            {aide.eligibilite === 'haute' ? '⭐ Éligible' : aide.eligibilite === 'moyenne' ? 'Possible' : 'À vérifier'}
                          </span>
                        </div>
                        
                        <p className="text-xs text-gray-600 mb-2">{aide.description}</p>
                        
                        {aide.organisme && (
                          <p className="text-xs text-amber-700 mb-2">📋 {aide.organisme}</p>
                        )}
                        
                        <div className="flex items-center justify-between text-xs mb-3">
                          <span className="font-semibold text-amber-600">{aide.montant}</span>
                          <span className="text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />{aide.deadline}
                          </span>
                        </div>
                        
                        <div className="flex gap-2">
                          {!aidesEnCours.includes(aide.id) ? (
                            <Button size="sm" className="flex-1 bg-amber-500 hover:bg-amber-600" onClick={() => demanderAide(aide)}>
                              <Check className="h-3 w-3 mr-1" />Suivre
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="flex-1 text-green-600 border-green-300" disabled>
                              <CheckCircle className="h-3 w-3 mr-1" />En suivi
                            </Button>
                          )}
                          {aide.url && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={aide.url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
